import ModalComponent from "../Modal/Modal"
import React, { useEffect, useState } from "react"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import {
  createPlan,
  changePlan,
  getAffiliateBilling,
  getBankAccountsDropdown,
} from "../../features/payment/paymentSlice"
import { useDispatch, useSelector } from "react-redux"
import { now, dateToServer } from "../../utils/date"
import { showSnackBar } from "../Snackbar/Snackbar"
import { isEmptyFields } from "../../helpers/helper"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { Frequency } from "../Frequency/Frequency"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"

export const GENERIC_GENERATE_PLAN_ERROR = "Something went wrong, please double-check the information you entered"

// WS 730201. With no bank account or card on file the Billing Account list opened empty and gave
// no reason. Behind PLAN_NO_BILLING_ACCOUNT_HINT the field is disabled and says where to add one.
// A client billed to the affiliate has neither by design, and the hint would send them to direct
// debit, so they are left as they were (QA 10-28).
export const NO_BILLING_ACCOUNT_HINT = "No bank account or card on file. Add one on the Billing tab first."
export const BILLING_ACCOUNTS_LOAD_ERROR = "Couldn't load the billing accounts. Please reload the page."
const NO_BILLING_ACCOUNTS = { bankAccounts: [], creditCards: [] }

// WS 2060104. Behind PLAN_GENERATE_REFUSE_DUPLICATE the server refuses a plan while the client
// already holds one, and the operator has to reset first. A rejected thunk resolves with the
// server's message as a plain-string payload; the generic text would send the operator back to a
// form that is fine.
export const generatePlanErrorText = ({ payload, showServerReason }) =>
  showServerReason && typeof payload === 'string' && payload ? payload : GENERIC_GENERATE_PLAN_ERROR

// WS 2074858. The same form changes the client's plan in place (PUT /plans/users/:userId, behind
// PLAN_CHANGE_IN_PLACE) instead of adding a second plan beside it. Its refusals ("a payment is
// still processing") are the whole answer, so they are always shown.
export const planModalMode = (mode = 'generate') => (mode === 'change'
  ? {
    buttonTitle: 'Change Plan',
    title: 'Change Payment Plan',
    lengthLabel: 'Payments in the new schedule',
    submit: changePlan,
    alwaysShowServerReason: true,
  }
  : {
    buttonTitle: 'Generate Plan',
    title: 'Payment Plan',
    lengthLabel: 'Program Length',
    submit: createPlan,
    alwaysShowServerReason: false,
  })

const GeneratePlanModal = ({ disableModal, userId, setIsGeneratePlan, alreadyPaid, dataPaymentsSchedule, userRole, mode }) => {
  const dispatch = useDispatch()
  const texts = planModalMode(mode)
  const refuseDuplicate = useSelector(selectFeatureFlag('PLAN_GENERATE_REFUSE_DUPLICATE'))
  const showServerReason = texts.alwaysShowServerReason || refuseDuplicate
  const noBillingAccountHint = useSelector(selectFeatureFlag('PLAN_NO_BILLING_ACCOUNT_HINT'))
  const { triggerLoading } = useMyContextLoading()
  const [bankUsersDropdown, setBankUsersDropdown] = useState([])
  // null once the server says the client has no bank account, card or affiliate method; the
  // affiliate method when they have one; undefined while loading or after a failure.
  const [affiliateBilling, setAffiliateBilling] = useState()
  const noBillingAccount = noBillingAccountHint && affiliateBilling === null
  const [result, setResult] = useState({
    plan: '',
    amount: '',
    frequency: '',
    cycleStartDate: now(),
    secondDate: '',
    billingAccountId: '',
    typeId: '',
    paymentDays: [],
    startFromMonth: null
  })

  const handleInputChange = ({ name, value }) => {
    setResult((prevData) => ({ ...prevData, [name]: value }))
  }

  const getDataGeneratePlan = async () => {
    triggerLoading(true)
    try {
      let bankUsersDropdownData = await dispatch(getBankAccountsDropdown(userId))
      const accounts = bankUsersDropdownData?.payload?.data
      // A failed request stored undefined here, and the Billing Account list then blanked the page.
      if (!accounts) showSnackBar("error", BILLING_ACCOUNTS_LOAD_ERROR)
      setBankUsersDropdown(accounts || NO_BILLING_ACCOUNTS)
      if (noBillingAccountHint && accounts && !accounts.bankAccounts?.length && !accounts.creditCards?.length) {
        const affiliate = await dispatch(getAffiliateBilling(userId))
        // A rejection carries a string payload, so its .data is undefined and stays "unknown".
        setAffiliateBilling(affiliate?.payload?.data)
      }
    } catch (error) {
      console.error('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const createNewPlan = async () => {
    triggerLoading(true)
    try {
      if (result?.frequency === "weekly" || result?.frequency === "monthly") {
        if (result?.frequency === "custom") {
          handleInputChange({ name: 'cycleStartDate', value: '' })
        }
        handleInputChange({ name: 'secondDate', value: '' })
      }
      const body = {
        ...result,
        cycleStartDate: dateToServer(result.cycleStartDate),
        secondDate: result.secondDate ? dateToServer(result.secondDate) : ''
      }
      let paymentTypesData = await dispatch(texts.submit({ userId, body }))
      setIsGeneratePlan(paymentTypesData?.payload?.status === "success")
      paymentTypesData?.payload?.status === "success"
        ? showSnackBar("success", paymentTypesData?.payload?.message)
        : showSnackBar("error", generatePlanErrorText({ payload: paymentTypesData?.payload, showServerReason }))
    } catch (error) {
      console.error('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  useEffect(() => {
    const fetchData = async () => {
      await getDataGeneratePlan()
    }

    fetchData()
  }, [])
  return (
    <ModalComponent disableStyleBtn={disableModal}
      disabled={isEmptyFields(result?.plan) || isEmptyFields(result?.amount) || isEmptyFields(result?.billingAccountId) || isEmptyFields(result?.cycleStartDate)}
      modalButtonTitle="Submit" title={texts.title}
      buttonClasses="border !border-primary-main h-10" buttonType="outlined"
      buttonTitle={texts.buttonTitle} functionButton={createNewPlan}
      subheader={<div className="text-md flex flex-row items-center">
        <span>Already Paid: </span>
        <span className="mx-3 text-primary-success-600">${alreadyPaid || 0}</span>

        <span>Total Debt Amount: </span>
        <span
          className="ml-3 text-primary-main">${dataPaymentsSchedule[0]?.accountBalance || 0}</span>
      </div>}>


      <Input placeholder={"Indicate number of payments"}
        label={texts.lengthLabel}
        type="Number"
        value={result?.plan}
        onChange={(e) => handleInputChange({ name: 'plan', value: e.target.value })}
      />
      <Input placeholder={"Indicate single payment amount"}
        parentClassName="mt-6"
        type="Number"
        label="Amount"
        leftSideIcon={<div className="text-text-secondary">$</div>}
        leftSideIconStyle="ml-5 mt-[17px]"
        value={result?.amount}
        onChange={(e) => handleInputChange({ name: 'amount', value: e.target.value })}
      />

      <Dropdown setValue={(e) => handleInputChange({ name: 'billingAccountId', value: e })}
        myListSubheader={[
          { name: 'Bank Accounts', value: 'bankAccounts' },
          { name: 'Credit Cards', value: 'creditCards' }
        ]}
        menuItems={bankUsersDropdown} value={result?.billingAccountId}
        disabled={noBillingAccount} helperText={noBillingAccount ? NO_BILLING_ACCOUNT_HINT : undefined}
        className="mt-6" variant="standard" label="Billing Account" />

      <Frequency paymentSchedule={result} setPaymentSchedule={setResult} userRole={userRole} />

    </ModalComponent>
  )
}

export default GeneratePlanModal
